import { getFirstImage } from 'utils/getFirstImage';
import { CellAlign, HeadCell } from '@dfl/mui-admin-layout';
import { AvatarNameCell } from 'modules/common/components/AvatarNameCell';
import { IProduct, IProductCategory, IProviderDate, ProductStock } from 'modules/inventory/product/interfaces/IProduct';
import { ProductStatusPicker } from 'modules/inventory/product/components/ProductStatusPicker';
import { ProductRowActions } from 'modules/inventory/product/components/ProductRowActions';
import { stockColumnAction } from './product.stock.columns';
import { RelatedProductRowActions } from '../components/RelatedProductRowActions';
import { PRODUCT_PERMISSIONS } from './product.permissions';
import { SUPPLIER_PERMISSIONS } from 'modules/inventory/provider/supplier/constants';
import { CATEGORY_PERMISSIONS } from 'modules/inventory/settings/category/constants';
import { WarehouseStockVisiblePicker } from 'modules/inventory/product-stock/components/StockVisiblePicker';
import FinalPriceCell from '../components/FinalPriceCell/FinalPriceCell';
import { ProductRules } from '../components/ProductRules';
import { productReservationColumn, productStockColumn } from './product-inventory.columns';
import CostCell from 'modules/common/components/CostCell/CostCell';
import { createdATColumn } from 'modules/common/constants';
import { stockColumn } from 'modules/dashboard/constant/table.columna';
import { warehouseColumn } from 'modules/inventory/product-stock/constants/data-error.columns';
import { CoverageCell } from 'modules/inventory/product-stock/components/CoverageCell';
import PriceCell from 'modules/security/audit-logs/components/TableCells/PriceCell';
import { Stack } from '@mui/material';
import { NumberValue } from '@dfl/mui-react-common';
import ProductStockView from '../components/ProductStockCell/ProductStockView';
import ProductTopPicker from 'modules/inventory/product/components/ProductTopPicker';

export const productNameColumn: HeadCell = {
  field: 'name',
  headerName: 'product:fields.name',
  disablePadding: false,
  renderCell: (name: string, data: IProduct) => (
    <AvatarNameCell
      link={`/inventory/products/${data?._id}/general`}
      name={name}
      variant={'rounded'}
      image={getFirstImage(data?.media)}
    />
  ),
};

export const productImageColumn: HeadCell = {
  field: 'media',
  headerName: 'product:fields.image',
  renderCell: (name: string, data: IProduct) => {
    const productId = data?.product || data?._id;
    return (
      <AvatarNameCell link={`/inventory/products/${productId}/general`} variant={'rounded'} image={getFirstImage(data.media)} />
    );
  },
};

export const productOnlyNameColumn: HeadCell = {
  field: 'name',
  headerName: 'product:fields.name',
  renderCell: (name: string, data: IProduct) => {
    const productId = data?.product || data?._id;
    return (
      <div>
        <AvatarNameCell
          link={`/inventory/products/${productId}/general`}
          hideImage
          name={data.name?.es || data?.name}
          permissions={PRODUCT_PERMISSIONS.PRODUCT_VIEW}
        />
        <ProductRules data={data} />
      </div>

    );
  },
};

export const productCodeColumn: HeadCell = {
  field: 'code',
  headerName: 'product:fields.code',
};

export const productBrandColumn: HeadCell = {
  field: 'brand',
  headerName: 'product:fields.brand',
};

export const productScoreColumn: HeadCell = {
  field: 'score',
  headerName: 'product:fields.priority',
};

export const productCostPriceColumn: HeadCell = {
  field: 'priceDetails.values.cost',
  headerName: 'product:fields.cost',
  permissions: PRODUCT_PERMISSIONS.PRODUCT_PRICE,
  renderCell: (value: number) => <CostCell value={value} />,
};

export const productPriceColumn: HeadCell = {
  field: 'finalPrice',
  headerName: 'product:fields.price',
  cellClassName: 'font-bold',
  width: 200,
  permissions: PRODUCT_PERMISSIONS.PRODUCT_PRICE,
  component: FinalPriceCell,
};

export const categoryNameColumn: HeadCell = {
  field: 'category',
  headerName: 'product:fields.category',
  disablePadding: false,
  renderCell: (category: IProductCategory) => (
    <AvatarNameCell
      link={`/inventory/settings/categories/${category?._id}/subcategories`}
      hideImage
      name={category?.name}
      permissions={[CATEGORY_PERMISSIONS.CATEGORY_VIEW]}
    />
  ),
};

export const supplierNameColumn: HeadCell = {
  field: 'providers.supplier',
  headerName: 'product:fields.supplier',
  disablePadding: false,
  renderCell: (supplier: IProviderDate) => {
    if (!supplier) return null;
    return (
      <AvatarNameCell
        link={`/inventory/provider/suppliers/${supplier?._id}/general`}
        hideImage
        name={supplier.name}
        permissions={[SUPPLIER_PERMISSIONS.SUPPLIER_VIEW]}
      />
    )
  },
};

export const visibleProductColumn: HeadCell = {
  field: 'visible',
  headerName: 'product:fields:visibility',
  renderCell: (visible, data) => <ProductStatusPicker value={visible} productId={data?._id} />,
};

export const productTopColumn: HeadCell = {
  field: 'internalSeo.top',
  headerName: 'product:fields:top',
  renderCell: (_value, data) => (
    <ProductTopPicker value={data?.internalSeo?.top} productId={data?._id} key={data?._id} />
  ),
};

export const orderProductColumn: HeadCell = {
  field: 'order',
  headerName: 'product:product.form.order.placeholder',
  align: CellAlign.CENTER,
};

export const categoryProductColumn: HeadCell = {
  field: 'category',
  headerName: 'common:category',
  renderCell: (category: any) => <>{category?.name}</>,
};

export const productRowActionColumn: HeadCell = {
  field: 'actions',
  headerName: 'common:actions',
  width: 150,
  align: CellAlign.CENTER,
  renderCell: (value, data: IProduct) => <RelatedProductRowActions rowId={data?._id} related={data?.related || []} />,
};

export const productActionsColumn: HeadCell = {
  field: 'actions',
  sortable: false,
  width: 100,
  headerName: 'actions',
  disablePadding: true,
  component: ProductRowActions,
  permissions: [PRODUCT_PERMISSIONS.PRODUCT_WRITE],
};

/* STOCK COLUMNS */
export const productStockCostPriceColumn: HeadCell = {
  field: 'cost',
  headerName: 'product:fields.cost',
  renderCell: (value: number) => <CostCell value={value} />,
  permissions: PRODUCT_PERMISSIONS.PRODUCT_PRICE,
};
export const productStockPriceColumn: HeadCell = {
  field: 'price',
  headerName: 'product:fields.price',
  cellClassName: 'font-bold',
  renderCell: (value: number) => <PriceCell value={value} />,
  permissions: PRODUCT_PERMISSIONS.PRODUCT_PRICE,
};

export const productAvailabilityColumn: HeadCell = {
  field: 'available',
  headerName: 'product:stock.stock',
  align: CellAlign.CENTER,
};

export const avgDailyUnitsColumn: HeadCell = {
  field: 'avgDailyUnits',
  disablePadding: false,
  headerName: 'dashboard:avgDailyUnits',
  align: CellAlign.CENTER,
};

export const coverageDaysColumn: HeadCell = {
  field: 'coverageDays',
  disablePadding: false,
  headerName: 'dashboard:coverageDays',
  renderCell: (value: number) => <CoverageCell coverage={value} />,
  align: CellAlign.CENTER,
};

export const supplierStockVisibilityColumn = (warehouseId: string) => ({
  field: 'visible',
  align: CellAlign.CENTER,
  headerName: 'warehouse:fields.visibility',
  renderCell: (visible: boolean, data: any) => (
    <WarehouseStockVisiblePicker value={visible} rowId={data?._id} warehouseId={warehouseId} />
  ),
});

export const productStockWarehouseColumn: HeadCell = {
  field: 'totalStock',
  headerName: 'product:section.inventory.available',
  width: 100,
  renderCell: (value: number, data: ProductStock) => (
    <Stack gap={1} flexDirection={'row'} alignItems={'center'} flexWrap={'wrap'}>
      <NumberValue value={value} sx={{ width: '40%', display: 'flex', justifyContent: 'center' }} />
      {!!data?.warehouseStock?.length && <ProductStockView warehouses={data?.warehouseStock} />}
    </Stack>
  )
};

// route: inventory/products
export const productColumns: HeadCell[] = [
  productImageColumn,
  productOnlyNameColumn,
  productCodeColumn,
  supplierNameColumn,
  visibleProductColumn,
  productTopColumn,
  productBrandColumn,
  productCostPriceColumn,
  productPriceColumn,
  productStockWarehouseColumn,
  categoryNameColumn,
  createdATColumn,
  productActionsColumn,
];

// route: inventory/provider/suppliers/:id/inventory > warehouses > productList
export const supplierInventoryStoreProductColumns = (warehouseId: string) => [
  productNameColumn,
  productCodeColumn,
  supplierStockVisibilityColumn(warehouseId),
  productStockCostPriceColumn,
  productStockPriceColumn,
  categoryProductColumn,
  createdATColumn,
  productAvailabilityColumn,
  productReservationColumn,
  productStockColumn,
  stockColumnAction,
];

// route: inventory/provider/suppliers/:id/products
export const supplierProductTabColumns: HeadCell[] = [
  productNameColumn,
  productCodeColumn,
  productCostPriceColumn,
  productBrandColumn,
  categoryProductColumn,
  visibleProductColumn,
  createdATColumn,
  // productRowActionColumn,
];

// route: inventory/settings/suppliers/:id/products
export const logisticProductTabColumns: HeadCell[] = [
  productNameColumn,
  productCodeColumn,
  productCostPriceColumn,
  productBrandColumn,
  categoryProductColumn,
  visibleProductColumn,
  createdATColumn,
  // productRowActionColumn,
];

// route: inventory/settings/categories/:id/products
export const categoryProductTabColumns: HeadCell[] = [
  productNameColumn,
  productCodeColumn,
  productBrandColumn,
  productCostPriceColumn,
  productPriceColumn,
  supplierNameColumn,
];

export const productCollectionElementColumns: HeadCell[] = [
  productImageColumn,
  productOnlyNameColumn,
  productCodeColumn,
  supplierNameColumn,
  productBrandColumn,
  productPriceColumn,
  categoryNameColumn,
  createdATColumn,
];

export const productByMarginColumns: HeadCell[] = [
  productNameColumn,
  productCodeColumn,
  supplierNameColumn,
  productCostPriceColumn,
  productPriceColumn,
];

export const productCoverageColumns: HeadCell[] = [
  productNameColumn,
  stockColumn,
  avgDailyUnitsColumn,
  coverageDaysColumn,
  warehouseColumn,
];
